import { GlobalStore, UserStore } from '@roc/feature-app-core';
import { borrowerExpMapping, LoanSubType, propertyTypes } from '@roc/feature-utils';
import { QuoteTypeStore } from './quoteTypeStore';
import PricerStore from './singlePropertyPricer/pricerStore';
import PricerSummaryStore from './singlePropertyPricer/pricerSummaryStore';
import { RentalPortfolioLoanEconomicsStore } from './loanEconomics/rentalPortfolioLoanEconomicsStore';
import { RentalPortfolioPropertiesStore } from './properties/rentalPortfolioPropertiesStore';
import { PropertyFormStore } from './properties/propertyFormStore';
import { PortfolioInformationFormStore } from './portfolioInformation/portfolioInformationFormStore';
import { action, makeObservable, override } from 'mobx';
import { LoanQuoteData } from '../types/LoanQuoteData';

export class RentalPortfolioQuoteStore extends QuoteTypeStore {

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    super(globalStore, userStore);
    this.loanSubtype = LoanSubType.RENTAL_PORTFOLIO;
    this.portfolioInformationFormStore = new PortfolioInformationFormStore(globalStore);
    this.propertiesStore = new RentalPortfolioPropertiesStore(
      this.globalStore, this.loanSubtype
    );
    this.loanEconomicsStore = new RentalPortfolioLoanEconomicsStore(
      this.globalStore,
      this,
      this.userStore
    );
    this.pricerStore = new PricerStore(globalStore, this, false);
    this.pricerSummaryStore = new PricerSummaryStore(globalStore, this, userStore);
    this.portfolioInformationFormStore = new PortfolioInformationFormStore(globalStore);
    this.propertiesStore = new RentalPortfolioPropertiesStore(globalStore, this.loanSubtype);

    makeObservable(this, {
      prepopulateProperties: action
    });

  }

  prepopulateProperties() {
    const propertyTypeFormStores = this.portfolioInformationFormStore.propertyTypeFormStores;

    if (!propertyTypeFormStores || propertyTypeFormStores.length === 0) {
      return;
    }

    const rentalPortfolioPropertiesStore = this.propertiesStore as RentalPortfolioPropertiesStore;
    const propertyTypes: any[] = propertyTypeFormStores.map(store => (store.getFlattenedValues()));

    const expectedPropertyTypesCounter = propertyTypes.reduce((acc, curr) => {
      const type = curr.propertyType;
      const count = +curr.numberOfProperties;
      acc[type] = (acc[type] || 0) + count;
      return acc;
    }, {} as Record<string, number>);

    const existingPropertyFormStoresByType = this.propertiesStore.propertyFormStores.reduce((acc, store) => {
      const type = store.form.fields.propertyType.value;
      (acc[type] = acc[type] || []).push(store);
      return acc;
    }, {} as Record<string, PropertyFormStore[]>);

    const allPropertyTypes = new Set([
      ...Object.keys(expectedPropertyTypesCounter),
      ...Object.keys(existingPropertyFormStoresByType),
    ]);

    for (const propertyType of allPropertyTypes) {
      const expected = expectedPropertyTypesCounter[propertyType] || 0;
      const existing = existingPropertyFormStoresByType[propertyType] || [];

      for (let i = existing.length; i < expected; i++) {
        rentalPortfolioPropertiesStore.addNewPropertyWithPropertyType(propertyType);
      }

      const excessCount = existing.length - expected;
      if (excessCount > 0) {
        const emptyOnes = existing.filter(store => !store.form.fields.address.value);
        const filledOnes = existing.filter(store => store.form.fields.address.value).reverse();

        [...emptyOnes, ...filledOnes]
          .slice(0, excessCount)
          .forEach(store => this.propertiesStore.removeProperty(store.form.meta.key));
      }
    }
  }

  reset() {
    this.portfolioInformationFormStore.reset();
    super.reset();
  }

  getLoanQuoteData(): LoanQuoteData {
    const loanQuoteData = super.getLoanQuoteData();
    const portfolioFields: any = this.portfolioInformationFormStore.getFlattenedValues();

    const propertyAddress = `${this.portfolioInformationFormStore.totalNoOfProperties} Property Portfolio in ${portfolioFields.propertyStates}`;
    const totalEstimatedAsIsValue = this.portfolioInformationFormStore.totalEstimatedAsIsValue;
    const propertyCounty = portfolioFields.propertyCounties.map(county => county.value.replace(/, \w{2}$/, "")).join(", ");
    const acquisitionDate = portfolioFields.lastPurchaseDate;
    const totalAcquisitionCost = portfolioFields.acquisitionPrice;
    const completedRenovations = portfolioFields.capitalImprovements;
    const monthlyExpenses = this.portfolioInformationFormStore.annualExpenses / 12;
    const monthlyRent = portfolioFields.totalGrossMonthlyRent;


    return {
      ...loanQuoteData,
      propertyAddress,
      totalEstimatedAsIsValue,
      propertyCounty,
      acquisitionDate,
      totalAcquisitionCost,
      completedRenovations,
      monthlyExpenses,
      monthlyRent,
    };
  }

}
